import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFoodLog, createUpload, fetchPartners } from "api";
import { PageHeader, FormField, Spinner, Select } from "components/common";
import { useAuth } from "hooks/useAuth";

interface FormState {
  partner_entity_id:      string;
  receiving_partner_id:   string;
  poundage:               string;
  description:            string;
  estimated_retail_value: string;
  completed_at:           string;
}

const INITIAL: FormState = {
  partner_entity_id:      "",
  receiving_partner_id:   "",
  poundage:               "",
  description:            "",
  estimated_retail_value: "",
  completed_at:           "",
};

export default function NewLogForm() {
  const navigate          = useNavigate();
  const queryClient       = useQueryClient();
  const [searchParams]    = useSearchParams();
  const { isAdmin, isCourier, partnerEntityId } = useAuth();

  const [form, setForm] = useState<FormState>({
    ...INITIAL,
    partner_entity_id: searchParams.get("partner_entity_id") ?? "",
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Fetch all partners for both dropdowns
  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
    enabled:  isAdmin || isCourier,
  });

  const donators     = partners.filter((p) => p.type === "donator" && p.active);
  const distributors = partners.filter((p) => p.type === "distributor" && p.active);

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const log = await createFoodLog({
        partner_entity_id:      Number(isAdmin || isCourier ? form.partner_entity_id : partnerEntityId),
        receiving_partner_id:   form.receiving_partner_id ? Number(form.receiving_partner_id) : undefined,
        poundage:               Number(form.poundage),
        description:            form.description || undefined,
        estimated_retail_value: form.estimated_retail_value ? Number(form.estimated_retail_value) : undefined,
        completed_at:           form.completed_at || undefined,
      });
      // Upload any selected images after the log is created
      for (const file of selectedFiles) {
        await createUpload("FoodLog", log.id, file);
      }
      return log;
    },
    onSuccess: (log) => {
      queryClient.invalidateQueries({ queryKey: ["foodLogs"] });
      navigate("/logs/" + log.id);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setSelectedFiles((prev) => [...prev, ...files]);
    // Reset so the same file can be re-added after removal
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) =>
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

  const isValid =
    form.poundage && Number(form.poundage) > 0 &&
    ((isAdmin || isCourier) ? !!form.partner_entity_id : true);

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-lg">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors"
      >
        ← Back
      </button>

      <PageHeader title="Add new log" subtitle="Record a food pickup." />

      <form
        onSubmit={(e) => { e.preventDefault(); if (isValid) mutate(); }}
        className="card p-6 space-y-5"
      >
        {/* Donating partner selector — admin/courier only */}
        {(isAdmin || isCourier) && (
          <FormField label="Donating partner">
            <Select value={form.partner_entity_id} onChange={set("partner_entity_id")}>
              <option value="" disabled>Select donating partner…</option>
              {donators.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </FormField>
        )}

        {/* Receiving partner selector — admin/courier only, distributors only */}
        {(isAdmin || isCourier) && (
          <FormField label="Received by">
            <Select value={form.receiving_partner_id} onChange={set("receiving_partner_id")}>
              <option value="">None (optional)</option>
              {distributors.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            <p className="text-xs text-gray-400 mt-1">
              The downstream partner receiving this food.
            </p>
          </FormField>
        )}

        <FormField label="Poundage (lbs)">
          <input
            className="input"
            type="number"
            min={1}
            step={1}
            value={form.poundage}
            onChange={set("poundage")}
            placeholder="e.g. 150"
            required
            autoFocus
          />
        </FormField>

        <FormField label="Estimated retail value ($)" >
          <input
            className="input"
            type="number"
            min={0}
            step={0.01}
            value={form.estimated_retail_value}
            onChange={set("estimated_retail_value")}
            placeholder="Optional"
          />
        </FormField>

        <FormField label="Completed at">
          <input
            className="input"
            type="datetime-local"
            value={form.completed_at}
            onChange={set("completed_at")}
          />
        </FormField>

        <FormField label="Description">
          <textarea
            className="input min-h-24 resize-y"
            value={form.description}
            onChange={set("description")}
            placeholder="Optional notes about this pickup…"
          />
        </FormField>

        {/* ── Image attachments ──────────────────────────────────────────────── */}
        <FormField label="Photos">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center gap-1.5 w-full rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
          >
            <span className="text-base leading-none">+</span> Add photos
          </button>

          {selectedFiles.length > 0 && (
            <ul className="mt-2 space-y-1">
              {selectedFiles.map((file, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-1.5 text-xs text-gray-700">
                  <span className="truncate max-w-[220px]">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="ml-2 shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-gray-400 mt-1">Optional — uploaded after the log is saved.</p>
        </FormField>

        {error && (
          <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || isPending}>
            {isPending
              ? <><Spinner className="h-4 inline-block mr-1" />{selectedFiles.length > 0 ? "Uploading…" : "Saving…"}</>
              : "Save log"}
          </button>
        </div>
      </form>
    </div>
  );
}
